import React, { createContext, use, ReactNode, useMemo, useCallback } from "react";
import { PermissionService } from "@/services/permissionService";
import { UserPermissions, RoleLevel, ROLE_LEVELS } from "@/types/permissions";
import { useAuth } from "./AuthContext";

interface PermissionContextType {
  permissions: UserPermissions | undefined;
  hasPermission: (resource: string, action: string) => boolean;
  hasPageAccess: (page: string) => boolean;
  getRoleLevel: () => RoleLevel;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

interface PermissionProviderProps {
  children: ReactNode;
}

/**
 * Permission context provider that wraps the application and provides RBAC functionality
 */
export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const permissionService = PermissionService.getInstance();

  const { currentUser: user } = useAuth();

  const hasPermission = useCallback(
    (resource: string, action: string): boolean => {
      if (!user.permissions) return false;

      // Use API permissions if available
      const resourceKey = resource.replace("-", "_");
      const resourceActions =
        user.permissions.actions[resourceKey as keyof typeof user.permissions.actions];
      if (resourceActions && typeof resourceActions === "object") {
        return resourceActions[action] === true;
      }

      return false;
    },
    [user],
  );

  const hasPageAccess = useCallback(
    (page: string): boolean => {
      if (!user.permissions) return false;
      return user.permissions.pages.includes(page);
    },
    [user.permissions],
  );

  // Get role level for compatibility with other hooks
  const getRoleLevel = useCallback((): RoleLevel => {
    if (!user.permissions?.role) return ROLE_LEVELS.EXTERNAL;

    // Convert role slug back to proper name for lookup
    const roleName = user.permissions.role
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    return permissionService.getRoleLevelFromName(roleName);
  }, [user.permissions.role, permissionService]);

  const value = useMemo(() => {
    return {
      permissions: user.permissions,
      hasPermission,
      hasPageAccess,
      getRoleLevel,
    };
  }, [user.permissions, getRoleLevel, hasPageAccess, hasPermission]);

  return <PermissionContext value={value}>{children}</PermissionContext>;
};

/**
 * Hook to use permission context
 */
// eslint-disable-next-line react-refresh/only-export-components
export const usePermissionContext = (): PermissionContextType => {
  const context = use(PermissionContext);
  if (!context) {
    throw new Error("usePermissionContext must be used within a PermissionProvider");
  }
  return context;
};

/**
 * Higher-order component for protecting components based on permissions
 */
interface WithPermissionProps {
  resource: string;
  action: string;
  fallback?: React.ComponentType | null;
  children: ReactNode;
}

export const WithPermission: React.FC<WithPermissionProps> = ({
  resource,
  action,
  fallback: Fallback = null,
  children,
}) => {
  const { hasPermission } = usePermissionContext();

  if (!hasPermission(resource, action)) {
    return Fallback ? <Fallback /> : null;
  }

  return <>{children}</>;
};

/**
 * Higher-order component for protecting pages based on page access
 */
interface WithPageAccessProps {
  page: string;
  fallback?: React.ComponentType | null;
  children: ReactNode;
}

export const WithPageAccess: React.FC<WithPageAccessProps> = ({
  page,
  fallback: Fallback = null,
  children,
}) => {
  const { hasPageAccess } = usePermissionContext();

  if (!hasPageAccess(page)) {
    return Fallback ? <Fallback /> : null;
  }

  return <>{children}</>;
};

/**
 * Higher-order component for protecting components based on minimum role level
 */
interface WithMinimumRoleProps {
  minimumLevel: RoleLevel;
  fallback?: React.ComponentType | null;
  children: ReactNode;
}

export const WithMinimumRole: React.FC<WithMinimumRoleProps> = ({
  minimumLevel,
  fallback: Fallback = null,
  children,
}) => {
  const { getRoleLevel } = usePermissionContext();

  if (getRoleLevel() < minimumLevel) {
    return Fallback ? <Fallback /> : null;
  }

  return <>{children}</>;
};

/**
 * Hook for conditional rendering based on permissions
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useConditionalRender = () => {
  const { hasPermission, hasPageAccess, getRoleLevel } = usePermissionContext();

  const renderIfPermission = (resource: string, action: string, component: ReactNode) => {
    return hasPermission(resource, action) ? component : null;
  };

  const renderIfPageAccess = (page: string, component: ReactNode) => {
    return hasPageAccess(page) ? component : null;
  };

  const renderIfMinimumRole = (minimumLevel: RoleLevel, component: ReactNode) => {
    return getRoleLevel() >= minimumLevel ? component : null;
  };

  return {
    renderIfPermission,
    renderIfPageAccess,
    renderIfMinimumRole,
  };
};
